/** @format **/
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import { Card, CardContent, Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import CardHeaderExt from '../../components/Heading/CardHeaderExt';
import I18n from '../../components/Translate/I18n';
import MvnoBlockListInterface from '../../interfaces/platform/MvnoBlockListInterface';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { MvnoBlockContainCollection } from '../../interfaces/platform/MvnoBlockContain';
import { MvnoBlockContainsJsonEdit } from './MvnoBlockContainsJsonEdit';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import { MvnoBlockContainsJsonAdd } from './MvnoBlockContainsJsonAdd';
import { NotificationHelper } from '../../helpers/NotificationHelper';

type MvnoBlockEditType = {
    handleSetBrandText: (text: string) => void;
};

export const MvnoBlockEdit: FunctionComponent<MvnoBlockEditType> = ({ handleSetBrandText }): React.ReactElement => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [mvnoBlockList, setMvnoBlockList] = useState<MvnoBlockListInterface | null>(null);
    const [mvnoBlockContains, setMvnoBlockContains] = useState<MvnoBlockContainCollection | null>(null);
    const [openAdd, setOpenAdd] = useState(false);

    const [sourceSkipSdg, setSourceSkipSdg] = useState<boolean>(false);
    const [sourceEnableOnlyForFullyActivateProduct, setSourceEnableOnlyForFullyActivateProduct] = useState<boolean>(false);

    useEffect(() => {
        if (mvnoBlockList === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getWithCallback(
                FormGeneralHelper.getByUri('/' + id),
                'mvno_block_list_edit_contains',
                (data: MvnoBlockListInterface) => {
                    setMvnoBlockList(data);
                    setSourceSkipSdg(data.skipSdgResponse)
                    setSourceEnableOnlyForFullyActivateProduct(data.enableOnlyForFullyActivateProduct)
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [dispatch, id, mvnoBlockList]);

    useEffect(() => {
        if (mvnoBlockList && mvnoBlockContains === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_block_contains')),
                'mvno_block_list_current_contains',
                [
                    { name: 'mvnoBlockListId', value: mvnoBlockList.id },
                    { name: 'itemsPerPage', value: 9999 },
                ],
                [{ name: 'positionId', value: 'asc' }],
                (data: MvnoBlockContainCollection) => setMvnoBlockContains(data),
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [dispatch, mvnoBlockList, mvnoBlockContains]);


    const savePosition = (): void => {
        const itemLoader = new ItemLoaders(dispatch);

        if (mvnoBlockList?.skipSdgResponse === sourceSkipSdg && mvnoBlockList?.enableOnlyForFullyActivateProduct === sourceEnableOnlyForFullyActivateProduct){
            return
        }


        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_block_lists/' + mvnoBlockList?.id)),
            'mvno_block_list_position',
            mvnoBlockList,
            (): void => {
                setSourceEnableOnlyForFullyActivateProduct(mvnoBlockList?.enableOnlyForFullyActivateProduct ?? false);
                setSourceSkipSdg(mvnoBlockList?.skipSdgResponse ?? false);

            },
            { propagateDataToCallback: true }
        );
    };

    return (
        <GridContainer>
            {mvnoBlockList && (
                <GridItem xs={12}>
                    <Card>
                        <CardHeaderExt
                            additionalContent={
                                <React.Fragment>
                                    <Typography variant="h6">
                                        <I18n trans="prov:block.block_contains_edit" valueObject={{ block_name: mvnoBlockList.blockName }} />
                                    </Typography>
                                    {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_BLOCK_CONTAINS_POST) && (

                                        <>
                                        <CustomTextButtom
                                            callback={(): void => setOpenAdd(true)}
                                            callbackParam={{}}
                                            label="prov:block.add_new_position"
                                            size="sm"
                                        />


                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={Boolean(mvnoBlockList.skipSdgResponse)}
                                                    name={'skipSdgResponse'}
                                                    onChange={(): void => {
                                                        setMvnoBlockList({
                                                            ...mvnoBlockList,
                                                            skipSdgResponse: !mvnoBlockList?.skipSdgResponse
                                                        })
                                                    }}
                                                />
                                            }
                                            label={<I18n trans={'prov:block.skip_sdg_response'} />}

                                        />
                                            <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={Boolean(mvnoBlockList.enableOnlyForFullyActivateProduct)}
                                                    name={'enableOnlyForFullyActivateProduct'}
                                                    onChange={(): void => {
                                                        setMvnoBlockList({
                                                            ...mvnoBlockList,
                                                            enableOnlyForFullyActivateProduct: !mvnoBlockList?.enableOnlyForFullyActivateProduct
                                                        })
                                                    }}
                                                />
                                            }
                                            label={<I18n trans={'prov:block.enable_only_for_fully_activate_product'} />}

                                        />
                                            <CustomTextButtom callback={savePosition} callbackParam={{}} label="general:common.save" size="sm" />

                                        </>


                                    )}

                                </React.Fragment>
                            }
                        />

                        <CardContent>
                            {mvnoBlockContains &&
                                mvnoBlockContains['hydra:member'].map((contains) => {
                                    return (
                                        <MvnoBlockContainsJsonEdit
                                            key={'mvno_list_contains_' + contains.id}
                                            mvnoBlockContains={contains}
                                            reloadContainsList={(): void => {
                                                setMvnoBlockContains(null);
                                            }}
                                        />
                                    );
                                })}
                        </CardContent>
                    </Card>
                    <MvnoBlockContainsJsonAdd
                        mvnoBlockList={mvnoBlockList}
                        reloadContainsList={(): void => setMvnoBlockContains(null)}
                        open={openAdd}
                        closeCallback={(): void => setOpenAdd(false)}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};
